import { apiService } from './api';
import {
  RentalUnit,
  CreateRentalUnitData,
  RentalUnitFilters,
  ApiResponse,
  PaginatedResponse,
  BulkImportResponse
} from '../types';

export const rentalUnitService = {
  // Basic CRUD operations
  async getRentalUnits(filters?: RentalUnitFilters): Promise<PaginatedResponse<RentalUnit>> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            value.forEach(v => params.append(key, String(v)));
          } else {
            params.append(key, String(value));
          }
        }
      });
    }

    const response = await apiService.get(`/rental-units?${params.toString()}`);
    return response.data;
  },

  async getRentalUnit(id: string): Promise<ApiResponse<RentalUnit>> {
    const response = await apiService.get(`/rental-units/${id}`);
    return response.data;
  },

  async createRentalUnit(data: CreateRentalUnitData): Promise<ApiResponse<RentalUnit>> {
    // Ensure client never sends true for inheritChargesFromProperty
    const payload = { ...data, inheritChargesFromProperty: false };
    const response = await apiService.post('/rental-units', payload);
    return response.data;
  },

  async updateRentalUnit(id: string, data: Partial<CreateRentalUnitData>): Promise<ApiResponse<RentalUnit>> {
    // Prevent sending true for inheritChargesFromProperty from client
    const payload = { ...data };
    if (payload.hasOwnProperty('inheritChargesFromProperty')) {
      payload.inheritChargesFromProperty = false;
    }
    const response = await apiService.put(`/rental-units/${id}`, payload);
    return response.data;
  },

  async deleteRentalUnit(id: string): Promise<ApiResponse> {
    const response = await apiService.delete(`/rental-units/${id}`);
    return response.data;
  },

  async updateRentalUnitVerificationStatus(id: string, status: 'pending' | 'verified' | 'rejected'): Promise<ApiResponse<RentalUnit>> {
    const response = await apiService.put(`/rental-units/${id}/verification-status`, { status });
    return response.data;
  },

  async updateRentalUnitSection(
    id: string,
    section: string,
    data: any,
    opts?: { cascadeToTenants?: boolean }
  ): Promise<ApiResponse<RentalUnit>> {
    const response = await apiService.put(`/rental-units/${id}/section`, {
      section,
      data,
      opts
    });
    return response.data;
  },

  // Search and filtering
  async searchRentalUnits(query: string, filters?: RentalUnitFilters): Promise<PaginatedResponse<RentalUnit>> {
    const params = new URLSearchParams({ q: query });
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            value.forEach(v => params.append(key, String(v)));
          } else {
            params.append(key, String(value));
          }
        }
      });
    }

    const response = await apiService.get(`/rental-units/search?${params.toString()}`);
    return response.data;
  },

  async getRentalUnitsByProperty(propertyId: string, filters?: {
    status?: string;
    isAvailable?: boolean;
  }): Promise<ApiResponse<RentalUnit[]>> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }

    const response = await apiService.get(`/rental-units/property/${propertyId}?${params.toString()}`);
    return response.data;
  },

  async getUnitsByProperties(propertyIds: string[]): Promise<ApiResponse<RentalUnit[]>> {
    const response = await apiService.post('/rental-units/by-properties', { propertyIds });
    return response.data;
  },

  // Get limited rental units by property (for selection UI - cached)
  async getLimitedRentalUnitsByProperty(propertyId: string): Promise<ApiResponse<{
    _id: string;
    floor: number;
    unitNumber: string;
    status: string;
  }[]>> {
    const response = await apiService.get(`/rental-units/property/${propertyId}/limited`);
    return response.data;
  },

  // Tenant management
  async getRentalUnitTenants(unitId: string, type: string = 'current'): Promise<ApiResponse<any[]>> {
    const response = await apiService.get(`/rental-units/${unitId}/tenants?type=${type}`);
    return response.data;
  },

  async addTenantToUnit(unitId: string, data: {
    userId: string;
    tenancyType: string;
    moveInDate: string;
    rentAmount: number;
    securityDeposit: number;
  }): Promise<ApiResponse> {
    const response = await apiService.post(`/rental-units/${unitId}/tenants`, data);
    return response.data;
  },

  async removeTenantFromUnit(unitId: string, tenantId: string): Promise<ApiResponse> {
    const response = await apiService.delete(`/rental-units/${unitId}/tenants/${tenantId}`);
    return response.data;
  },

  async updateTenantInUnit(unitId: string, tenantId: string, data: any): Promise<ApiResponse> {
    const response = await apiService.put(`/rental-units/${unitId}/tenants/${tenantId}`, data);
    return response.data;
  },

  // Analytics and management
  async getRentalUnitAnalytics(unitId: string): Promise<ApiResponse<{
    totalViews: number;
    totalApplications: number;
    averageStayDuration: number;
    revenueGenerated: number;
    occupancyRate: number;
  }>> {
    const response = await apiService.get(`/rental-units/${unitId}/analytics`);
    return response.data;
  },

  async toggleAvailability(unitId: string, isAvailable: boolean): Promise<ApiResponse<RentalUnit>> {
    const response = await apiService.put(`/rental-units/${unitId}/availability`, { isAvailable });
    return response.data;
  },

  // Image management
  async uploadImages(unitId: string, files: File[]): Promise<ApiResponse<{
    images: { url: string; category: string; isPrimary: boolean }[];
  }>> {
    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append('images', file);
    });

    const response = await apiService.post(`/rental-units/${unitId}/images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  async deleteImage(unitId: string, imageId: string): Promise<ApiResponse> {
    const response = await apiService.delete(`/rental-units/${unitId}/images/${imageId}`);
    return response.data;
  },

  // Verification
  async verifyRentalUnit(unitId: string): Promise<ApiResponse<RentalUnit>> {
    const response = await apiService.put(`/rental-units/${unitId}/verify`);
    return response.data;
  },

  // Status update
  async updateRentalUnitStatus(data: {
    companyIds?: string[];
    propertyId?: string;
    unitId?: string;
  }): Promise<ApiResponse<{
    totalUnitsProcessed: number;
    unitsUpdated: Array<{ unitId: string; oldStatus: string; newStatus: string }>;
    errors: Array<{ unitId: string; error: string }>;
  }>> {
    const response = await apiService.post('/rental-units/update-status', data);
    return response.data;
  },

  // Bulk import rental units
  async bulkImportRentalUnits(rentalUnits: CreateRentalUnitData[]): Promise<BulkImportResponse<RentalUnit>> {
    // Ensure imported units cannot enable inheritance
    const safeUnits = (rentalUnits || []).map(u => ({ ...u, inheritChargesFromProperty: false }));
    const response = await apiService.post('/rental-units/bulk-import', { rentalUnits: safeUnits });
    return response.data;
  },

  // Helper methods
  calculateMonthlyTotal(unit: RentalUnit): number {
    return unit.pricing.rent + (unit.pricing.maintenanceCharges.included ? 0 : unit.pricing.maintenanceCharges.amount);
  },

  isAvailable(unit: RentalUnit): boolean {
    return unit.availability.isAvailable && unit.status === 'Available';
  },

  getAvailabilityText(unit: RentalUnit): string {
    if (!unit.availability.isAvailable) return 'Not Available';
    if (unit.status === 'Occupied') return 'Occupied';
    if (unit.status === 'Under_Maintenance') return 'Under Maintenance';
    if (unit.status === 'Renovation') return 'Renovation';
    return 'Available';
  },

  getPrimaryImage(unit: RentalUnit): string | null {
    const primary = unit.images.find(img => img.isPrimary);
    return primary ? primary.url : (unit.images.length > 0 ? unit.images[0].url : null);
  }
};